import { formatDisplayNumber, getCurrencyConfig } from '@stokr/components-library'

/**
 * The currencies an investor can pay in, as the invest flow (invest-in-sto `domain/currency.ts`)
 * supports them: back-office refs, the rate each converts with, a name for the menu. A ref the
 * flow does not support is never offered here.
 */
const SUPPORTED = {
  euro: { name: 'Euro', rateKey: 'EUR' },
  usd: { name: 'USD', rateKey: 'USD' },
  chf: { name: 'CHF', rateKey: 'CHF' },
  bitcoin: { name: 'Bitcoin', rateKey: 'BTC' },
  lbtc: { name: 'Liquid Bitcoin', rateKey: 'BTC' },
  'btc-fb': { name: 'Bitcoin', rateKey: 'BTC' },
  'usdc-fb': { name: 'USDC (Ethereum)', rateKey: 'USD' },
  'usdq-fb': { name: 'USDQ (Ethereum)', rateKey: 'USD' },
} as const satisfies Record<string, { name: string; rateKey: RateKey }>

type RateKey = 'EUR' | 'USD' | 'CHF' | 'BTC'
export type InvestCurrencyRef = keyof typeof SUPPORTED

export interface InvestCurrency {
  /** Back-office ref (`lbtc`), what the invest flow is handed. */
  ref: InvestCurrencyRef
  /** Short code for the field (`LBTC`, `USD`). */
  code: string
  /** Name in the menu (`Liquid Bitcoin`). */
  name: string
  /** `€`, `$`; the code again for a currency written after its amount (`LBTC`). */
  sign: string
  decimals: number
  rateKey: RateKey
}

const isSupported = (ref: string): ref is InvestCurrencyRef => Object.prototype.hasOwnProperty.call(SUPPORTED, ref)

/**
 * The project's payment currencies, in back-office order, from the refs it lists
 * (`acceptedCurrencyRefs`). Unknown or repeated refs are left out.
 */
export const toInvestCurrencies = (refs: unknown[] | undefined): InvestCurrency[] => {
  const seen = new Set<string>()
  return (refs ?? []).flatMap((raw) => {
    const ref = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
    if (!isSupported(ref) || seen.has(ref)) return []
    seen.add(ref)
    const { symbol, sign, decimals } = getCurrencyConfig(ref)
    return [{ ref, code: symbol, sign, name: SUPPORTED[ref].name, decimals, rateKey: SUPPORTED[ref].rateKey }]
  })
}

/** Project currency → its own ref (USD → `usd`), preferred over a token priced the same (USDQ). */
const OWN_REF: Record<string, InvestCurrencyRef> = { USD: 'usd', EUR: 'euro', CHF: 'chf', BTC: 'bitcoin' }

/** The project's own currency when it is offered, else one priced in it (USDC), else the first. */
export const defaultInvestCurrency = (
  currencies: InvestCurrency[],
  tokenCurrency: string | undefined,
): InvestCurrency | undefined => {
  const code = tokenCurrency?.toUpperCase() ?? ''
  return (
    currencies.find((currency) => currency.ref === OWN_REF[code]) ??
    currencies.find((currency) => currency.rateKey === code) ??
    currencies[0]
  )
}

/** `/prices/exchangerates` (EUR-based) and the BTC price in USD (`/prices/btcPrice`). */
export interface RawRates {
  rates: Record<string, number>
  btcInUsd: number
}

/**
 * How much of the project's currency one unit of each rate key costs: invest-in-sto's
 * `normalizeRates`, so the page estimates as the flow charges. Undefined for a currency it cannot
 * convert into, BTC included: the flow prices a BTC project with EUR maths (1 EUR = 1 BTC), so
 * such a project shows no estimate in another currency rather than a wrong one.
 */
export const toPricesInProjectCurrency = (
  raw: RawRates,
  tokenCurrency: string | undefined,
): Record<string, number> | undefined => {
  const currency = tokenCurrency?.toUpperCase()
  if (currency !== 'USD' && currency !== 'EUR') return undefined
  const prices: Record<string, number> = { EUR: 1 }
  for (const [symbol, rate] of Object.entries(raw.rates)) {
    if (rate > 0) prices[symbol] = 1 / rate
  }
  if (currency === 'EUR') {
    prices.BTC = raw.btcInUsd * (prices.USD ?? Number.NaN)
    return prices
  }
  const usd = prices.USD
  if (!usd) return undefined
  for (const symbol of Object.keys(prices)) prices[symbol] /= usd
  prices.BTC = raw.btcInUsd
  return prices
}

/** One unit of `currency` in the project's currency: 1 for its own; undefined while rates load. */
export const projectCurrencyRate = (
  currency: InvestCurrency,
  tokenCurrency: string | undefined,
  prices: Record<string, number> | undefined,
): number | undefined => {
  if (currency.rateKey === tokenCurrency?.toUpperCase()) return 1
  const rate = prices?.[currency.rateKey]
  return rate !== undefined && Number.isFinite(rate) && rate > 0 ? rate : undefined
}

/** `€89.40`, `0.00121044 BTC`: the currency's sign before, or its code after, at its decimals. */
export const formatInvestAmount = (value: number, currency: InvestCurrency): string => {
  const number = formatDisplayNumber(value, {
    minimumFractionDigits: currency.sign !== currency.code ? currency.decimals : 0,
    maximumFractionDigits: currency.decimals,
  })
  return currency.sign !== currency.code ? `${currency.sign}${number}` : `${number} ${currency.code}`
}
